/**
 * Comms data access: conversations (staff and self-service), sending, quick coaching messages,
 * AI drafting and translation, public view links, my notification preferences and
 * acknowledgements. Other modules (coaching, me) use these hooks.
 */
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from '@/lib/api/client';
import type { components } from '@/lib/api/schema';

type S = components['schemas'];
export type ConversationOut = S['ConversationOut'];
export type ConversationDetail = S['ConversationDetail'];
export type MessageOut = S['MessageOut'];
export type IntentOut = S['IntentOut'];
export type QuickActionDraftOut = S['QuickActionDraftOut'];
export type SendMessageBody = S['SendMessage'];
export type QuickActionBody = S['QuickAction'];
export type PublicMessageOut = S['PublicMessageOut'];
export type NotificationPrefsOut = S['NotificationPrefsOut'];
export type NotificationPrefsPatch = S['NotificationPrefsPatch'];
export type PendingNotificationOut = S['PendingNotificationOut'];
export type Channel = 'inapp' | 'email' | 'sms' | 'whatsapp' | 'push';
export type NotifyCategory = NonNullable<NotificationPrefsPatch['muted']>[number];

export const commsKeys = {
  all: ['comms'] as const,
  conversations: (p: object) => ['comms', 'conversations', p] as const,
  conversation: (id: string) => ['comms', 'conversation', id] as const,
  quick: (driverId: string, p: object) => ['comms', 'quick', driverId, p] as const,
  mine: ['me', 'messages'] as const,
  myConversation: (id: string) => ['me', 'messages', id] as const,
  channels: ['me', 'messages', 'channels'] as const,
  notificationPrefs: ['me', 'notifications', 'preferences'] as const,
  pendingAcks: ['me', 'notifications', 'pending'] as const,
  public: (token: string) => ['public', 'message', token] as const,
};

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: commsKeys.all });
    void qc.invalidateQueries({ queryKey: ['me'] });
    void qc.invalidateQueries({ queryKey: ['coaching'] });
  };
}

// ---------------------------------------------------------------- staff

export interface ConversationQuery {
  driver?: string;
  subject_type?: string;
  subject_id?: string;
  status?: 'open' | 'closed';
  unread?: boolean;
}

export function useConversations(p: ConversationQuery, enabled = true) {
  return useInfiniteQuery({
    queryKey: commsKeys.conversations(p),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/comms/conversations', { params: { query: { ...p, cursor: pageParam, limit: 100 } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useConversation(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: commsKeys.conversation(id ?? ''),
    queryFn: () => unwrap(api.GET('/api/comms/conversations/{conversation_id}', { params: { path: { conversation_id: id ?? '' }, query: { limit: 100 } } })),
    enabled: !!id && enabled,
    refetchInterval: 30_000,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrap(api.POST('/api/comms/conversations/{conversation_id}/read', { params: { path: { conversation_id: id } } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['comms', 'conversations'] }),
  });
}

export function useSendMessage() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (body: SendMessageBody) => unwrap(api.POST('/api/comms/messages', { body })), onSuccess: invalidate });
}

export function useCreateLink() {
  return useMutation({
    mutationFn: (messageId: string) => unwrap(api.POST('/api/comms/messages/{message_id}/link', { params: { path: { message_id: messageId } } })),
  });
}

export function useQuickActionDraft(driverId: string | undefined, p: { language?: string; days?: number }) {
  return useQuery({
    queryKey: commsKeys.quick(driverId ?? '', p),
    queryFn: () => unwrap(api.GET('/api/comms/quick-action/{driver_id}', { params: { path: { driver_id: driverId ?? '' }, query: p } })),
    enabled: !!driverId,
  });
}

export function useSendQuickAction() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (body: QuickActionBody) => unwrap(api.POST('/api/comms/quick-action', { body })), onSuccess: invalidate });
}

export function useDraft() {
  return useMutation({
    mutationFn: (body: S['CommsDraftIn']) => unwrap(api.POST('/api/comms/draft', { body })),
  });
}

export function useTranslate() {
  return useMutation({
    mutationFn: (body: { text: string; language: 'en' | 'nl' | 'fr' }) => unwrap(api.POST('/api/comms/translate', { body })),
  });
}

// ---------------------------------------------------------------- self-service (/api/me/messages)

export function useMyConversations(enabled = true) {
  return useInfiniteQuery({
    queryKey: commsKeys.mine,
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/me/messages', { params: { query: { cursor: pageParam, limit: 50 } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled,
    refetchInterval: 60_000,
  });
}

export function useMyConversation(id: string) {
  return useQuery({
    queryKey: commsKeys.myConversation(id),
    queryFn: () => unwrap(api.GET('/api/me/messages/{conversation_id}', { params: { path: { conversation_id: id }, query: { limit: 100 } } })),
    refetchInterval: 30_000,
  });
}

export function useMyReply(id: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (text: string) => unwrap(api.POST('/api/me/messages/{conversation_id}/reply', { params: { path: { conversation_id: id } }, body: { text } })),
    onSuccess: invalidate,
  });
}

export function useMyRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrap(api.POST('/api/me/messages/{conversation_id}/read', { params: { path: { conversation_id: id } } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: commsKeys.mine, exact: true }),
  });
}

export function useMyChannels(enabled = true) {
  return useQuery({ queryKey: commsKeys.channels, queryFn: () => unwrap(api.GET('/api/me/messages/channels')), enabled });
}

export function useSetMyChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { channel: 'email' | 'sms' | 'whatsapp' | 'push' | 'all'; opt_out: boolean }) => unwrap(api.POST('/api/me/messages/channels', { body })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: commsKeys.channels }),
  });
}

// ---------------------------------------------------------------- my notifications (/api/me/notifications)

export function useMyNotificationPrefs(enabled = true) {
  return useQuery({ queryKey: commsKeys.notificationPrefs, queryFn: () => unwrap(api.GET('/api/me/notifications/preferences')), enabled });
}

/**
 * The channels this server actually sends through (`live_channels`: in the app always, email when
 * mail is configured). Messages on the other channels are stored and shown in the app, never
 * sent. Read from my notification settings (every signed-in person may read their own); `known`
 * is false until it has loaded, so nothing is marked on a guess.
 */
export function useLiveChannels(enabled = true): { known: boolean; live: string[]; isLive: (channel: string) => boolean } {
  const q = useMyNotificationPrefs(enabled);
  const live = q.data?.live_channels ?? [];
  return { known: !!q.data, live, isLive: (channel: string) => channel === 'inapp' || live.includes(channel) };
}

/** Save only what changed (the server writes only the fields sent; null resets one to the company default). */
export function useSaveNotificationPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NotificationPrefsPatch) => unwrap(api.PATCH('/api/me/notifications/preferences', { body })),
    onSettled: () => void qc.invalidateQueries({ queryKey: commsKeys.notificationPrefs }),
  });
}

/** Notifications sent to me that ask for my acknowledgement (the bell and Home list them). */
export function pendingAcksQuery() {
  return {
    queryKey: commsKeys.pendingAcks,
    queryFn: () => unwrap(api.GET('/api/me/notifications/pending', { params: { query: { limit: 50 } } })),
    refetchInterval: 60_000,
  };
}

export function useAcknowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => unwrap(api.POST('/api/me/notifications/{message_id}/acknowledge', { params: { path: { message_id: messageId } } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me'] });
      void qc.invalidateQueries({ queryKey: commsKeys.all });
    },
  });
}

// ---------------------------------------------------------------- public link

export function usePublicMessage(token: string) {
  return useQuery({
    queryKey: commsKeys.public(token),
    queryFn: () => unwrap(api.GET('/api/public/m/{token}', { params: { path: { token } } })),
    retry: false,
  });
}

export function usePublicAcknowledge(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST('/api/public/m/{token}/acknowledge', { params: { path: { token } } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: commsKeys.public(token) }),
  });
}
